import { AlertCircle, RefreshCw, X } from 'lucide-react';
import { Button } from '../ui/button';
import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
} from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import FormattedMessage from '../formatted-message';

interface DefaultAsyncErrorFallbackProps {
    error: Error | null;
    resetErrorBoundary: () => void;
    cancelErrorBoundary: () => void;
    isResetting: boolean;
    showCancelButton?: boolean;
}

export default function DefaultAsyncErrorFallback({
    error,
    resetErrorBoundary,
    cancelErrorBoundary,
    isResetting,
    showCancelButton = true,
}: DefaultAsyncErrorFallbackProps) {
    const isNetworkError =
        error?.message?.includes('Network') ||
        error?.message?.includes('fetch') ||
        error?.message?.includes('axios');

    return (
        <Card className='w-full py-2 mx-auto relative'>
            {showCancelButton && (
                <Button
                    variant='ghost'
                    size='icon'
                    className='absolute right-2 top-2 h-6 w-6'
                    onClick={cancelErrorBoundary}>
                    <X className='h-4 w-4' />
                </Button>
            )}

            <CardHeader>
                <CardTitle className='flex items-center gap-2 text-destructive'>
                    <AlertCircle className='h-5 w-5' />
                    Operation Failed
                </CardTitle>
            </CardHeader>

            <CardContent className='space-y-4'>
                <Alert variant='destructive'>
                    <AlertCircle className='h-4 w-4' />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>
                        <FormattedMessage
                            message={
                                error?.message || 'An unexpected error occurred'
                            }
                        />
                    </AlertDescription>
                </Alert>

                {isNetworkError && (
                    <Alert>
                        <AlertCircle className='h-4 w-4' />
                        <AlertTitle>Network Error</AlertTitle>
                        <AlertDescription>
                            Please check your internet connection and try again.
                        </AlertDescription>
                    </Alert>
                )}

                {import.meta.env.MODE === 'development' && error?.stack && (
                    <details className='mt-4'>
                        <summary className='cursor-pointer text-sm text-muted-foreground'>
                            Stack Trace
                        </summary>
                        <pre className='mt-2 text-xs bg-muted p-2 rounded overflow-auto max-h-40'>
                            {error.stack}
                        </pre>
                    </details>
                )}
            </CardContent>

            <CardFooter className='flex gap-2'>
                {showCancelButton && (
                    <Button
                        variant='outline'
                        onClick={cancelErrorBoundary}
                        className='flex-1 gap-2 h-10'>
                        <X className='size-5' />
                        Cancel
                    </Button>
                )}

                <Button
                    onClick={resetErrorBoundary}
                    disabled={isResetting}
                    className={`${showCancelButton ? 'flex-1' : 'w-full'} h-10`}
                    variant='default'>
                    {isResetting ? (
                        <RefreshCw className='h-4 w-4 animate-spin' />
                    ) : (
                        <RefreshCw className='h-4 w-4' />
                    )}
                    {isResetting ? 'Retrying...' : 'Try Again'}
                </Button>
            </CardFooter>
        </Card>
    );
}
